import { useEffect, useState } from "react";
import api, { API } from "../../lib/api";
import { PageHeader, Loading, EmptyState } from "../../components/shared";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";
import { Wrench, FileCheck, Download, Send, ShieldCheck } from "lucide-react";

export default function AdminVendors() {
  const [rows, setRows] = useState(null);
  const [detail, setDetail] = useState(null);
  const load = () => api.get("/admin/vendors").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const viewVendor = async (vendorId) => {
    try {
      const response = await api.get(`/admin/vendors/${vendorId}/360`);
      setDetail(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not load vendor details");
    }
  };
  const updateVerification = async (status) => {
    try {
      await api.patch(`/admin/vendors/${detail.vendor.vendor_id}/verification`, { status });
      toast.success("Verification updated");
      viewVendor(detail.vendor.vendor_id);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not update verification");
    }
  };
  const updateAccount = async (account_status) => {
    try {
      await api.patch(`/admin/vendors/${detail.vendor.vendor_id}/account`, { account_status });
      toast.success("Account status updated");
      viewVendor(detail.vendor.vendor_id);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not update account");
    }
  };
  const resendActivation = async () => {
    try {
      const response = await api.post(`/admin/vendors/${detail.vendor.vendor_id}/activation`);
      if (response.data.delivery_status === "sent") {
        toast.success(response.data.message);
      } else {
        toast.warning(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not issue activation");
    }
  };
  const reviewDocument = async (document, review_status) => {
    const reason = review_status === "REJECTED" ? window.prompt("Safe correction reason") || "" : "";
    if (review_status === "REJECTED" && !reason) return;
    try {
      await api.patch(`/admin/vendors/documents/${document.vdoc_id}`, { review_status, reason });
      toast.success("Document review saved");
      viewVendor(detail.vendor.vendor_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not review document");
    }
  };
  if (!rows) return <Loading />;
  return (
    <div>
      <PageHeader title="Vendors" subtitle="Verify and manage service partners." />
      {rows.length === 0 ? <EmptyState icon={Wrench} title="No vendors yet" subtitle="Registered vendors appear here for verification." /> : (
        <div className="bg-card border rounded-xl overflow-hidden overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Business</TableHead><TableHead>Contact</TableHead><TableHead>City</TableHead><TableHead>Verification</TableHead><TableHead>Account</TableHead><TableHead>Profile</TableHead><TableHead>Action</TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.map((v) => (
                <TableRow key={v.vendor_id} data-testid={`admin-vendor-${v.vendor_id}`}>
                  <TableCell className="font-medium">{v.business_name}</TableCell>
                  <TableCell>{v.contact_person}<div className="text-xs text-muted-foreground">{v.mobile}</div></TableCell>
                  <TableCell>{v.city}</TableCell>
                  <TableCell data-testid={`vendor-verification-${v.vendor_id}`}>{v.vendor_verification_status || "REVIEW REQUIRED"}</TableCell>
                  <TableCell data-testid={`vendor-account-${v.vendor_id}`}>{v.account_status || "ACTIVE"}</TableCell>
                  <TableCell data-testid={`vendor-completion-${v.vendor_id}`}>{v.profile_completion ?? 0}%</TableCell>
                  <TableCell><Button size="sm" variant="outline" className="gap-1.5 rounded-full" onClick={() => viewVendor(v.vendor_id)} data-testid={`vendor-360-${v.vendor_id}`}><FileCheck className="w-3.5 h-3.5" /> Review</Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <Dialog open={!!detail} onOpenChange={(open) => !open && setDetail(null)}>
        <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto" data-testid="vendor-360-dialog">
          <DialogHeader><DialogTitle className="font-display">{detail?.vendor.business_name} — Vendor 360</DialogTitle></DialogHeader>
          {detail && <div className="space-y-5 text-sm">
            <div className="grid gap-3 border-y py-4 sm:grid-cols-3" data-testid="vendor-360-statuses">
              <Status label="Application" value={detail.vendor.application_status} />
              <Status label="Verification" value={detail.vendor.vendor_verification_status} />
              <Status label="Account" value={detail.vendor.account_status} />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => updateVerification("VERIFIED")} data-testid="vendor-360-verify"><ShieldCheck className="mr-1 h-4 w-4" />Verify</Button>
              <Button size="sm" variant="outline" onClick={() => updateVerification("DOCUMENTS_PENDING")} data-testid="vendor-360-docs-pending">Request documents</Button>
              {detail.vendor.account_status !== "ACTIVE" && <Button size="sm" variant="outline" onClick={resendActivation} data-testid="vendor-360-resend-activation"><Send className="mr-1 h-4 w-4" />Resend activation</Button>}
              {detail.vendor.account_status === "SUSPENDED" ? <Button size="sm" variant="outline" onClick={() => updateAccount("ACTIVE")} data-testid="vendor-360-reactivate">Reactivate</Button> : <Button size="sm" variant="outline" className="text-rose-700" onClick={() => updateAccount("SUSPENDED")} data-testid="vendor-360-suspend">Suspend account</Button>}
            </div>
            <section>
              <h3 className="font-semibold">Services ({detail.services.length}) · Areas ({detail.service_areas.length})</h3>
              <p className="mt-1 text-xs text-muted-foreground">Profile completion: {detail.vendor.profile_completion.percentage}%</p>
            </section>
            <section>
              <h3 className="font-semibold">Verification documents</h3>
              {detail.documents.length === 0 ? <p className="mt-2 text-muted-foreground">No documents uploaded.</p> : <div className="mt-3 space-y-2">{detail.documents.map((document) => (
                <div className="flex flex-wrap items-center justify-between gap-3 border p-3" data-testid={`vendor-360-document-${document.vdoc_id}`} key={document.vdoc_id}>
                  <div><p className="font-medium">{document.doc_type}</p><p className="text-xs text-muted-foreground">{document.filename} · {document.review_status || "PENDING"}</p></div>
                  <div className="flex gap-2"><Button size="sm" variant="outline" onClick={() => window.open(`${API}/admin/vendors/documents/${document.vdoc_id}/download`, "_blank")} data-testid={`vendor-360-document-download-${document.vdoc_id}`}><Download className="h-3.5 w-3.5" /></Button><Button size="sm" variant="outline" onClick={() => reviewDocument(document, "VERIFIED")} data-testid={`vendor-360-document-verify-${document.vdoc_id}`}>Verify</Button><Button size="sm" variant="outline" onClick={() => reviewDocument(document, "REJECTED")} data-testid={`vendor-360-document-reject-${document.vdoc_id}`}>Reject</Button></div>
                </div>
              ))}</div>}
            </section>
            <section className="border-t pt-4"><h3 className="font-semibold">Subscription</h3><p className="mt-1">{detail.subscription.effective_status} · {detail.subscription.plan || "monthly"}</p></section>
          </div>}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Status({ label, value }) {
  return <div><p className="text-xs text-muted-foreground">{label}</p><p className="mt-1 font-semibold">{String(value || "Not available").replace(/_/g, " ")}</p></div>;
}
